/**
 * React Hook: Sync Metadata Item from Salesforce
 * 
 * Fetches the latest version of a single metadata item from Salesforce.
 * This is a direct mutation call - no WebSocket needed.
 * 
 * Response cases:
 * - Content returned: File was updated in Salesforce
 * - null + "No changes found": File is already up to date
 * - Error: Actual error occurred
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

// ============================================================================
// Types
// ============================================================================

export interface SyncMetadataItemInput {
  /** Organization ID */
  organizationId: string;
  /** Retrieval ID */
  retrievalId: string;
  /** Metadata Item ID to sync */
  metadataItemId: string;
}

export interface SyncMetadataItemResult {
  id: string;
  content: string;
  metadataType?: string;
  fullName?: string;
  sha256Hash?: string;
}

export interface SyncMetadataItemResponse {
  fetchMetadataItemFromSalesforce: {
    metadataItem: SyncMetadataItemResult | null;
    message: string;
  };
}

// Special error for "no changes" case
export class NoChangesError extends Error {
  constructor(message: string = 'No changes found in Salesforce. Content is already up to date.') {
    super(message);
    this.name = 'NoChangesError';
  }
}

// ============================================================================
// GraphQL Mutation
// ============================================================================

const SYNC_METADATA_ITEM_MUTATION = `
  mutation FetchMetadataItem($input: FetchMetadataItemFromSalesforceInput!) {
    fetchMetadataItemFromSalesforce(input: $input) {
      metadataItem {
        id
        content
        metadataType
        fullName
        sha256Hash
      }
      message
    }
  }
`;

// ============================================================================
// Hook
// ============================================================================

interface UseSyncMetadataItemOptions {
  /** Callback when sync succeeds with new content */
  onSuccess?: (data: SyncMetadataItemResult) => void;
  /** Callback when file is already up to date (no changes) */
  onNoChanges?: () => void;
  /** Callback when sync fails */
  onError?: (error: Error) => void;
}

/**
 * Hook to sync a single metadata item from Salesforce
 * 
 * @example
 * ```tsx
 * const { syncItem, isPending } = useSyncMetadataItem({
 *   onSuccess: (data) => {
 *     console.log('New content:', data.content);
 *     updateEditorContent(data.content);
 *   },
 *   onNoChanges: () => {
 *     toast.success('File is already up to date');
 *   },
 *   onError: (error) => {
 *     toast.error(`Sync failed: ${error.message}`);
 *   },
 * });
 * 
 * // Trigger sync
 * await syncItem({ metadataItemId: 'item-123' });
 * ```
 */
export function useSyncMetadataItem(options?: UseSyncMetadataItemOptions) {
  const queryClient = useQueryClient();
  
  // Get org data from auth store
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const orgId = selectedOrg?.id;
  const retrievalId = selectedOrg?.latestMetadataRetrieval?.id;

  const mutation = useMutation({
    mutationFn: async (input: { metadataItemId: string }) => {
      if (!orgId || !retrievalId) {
        throw new Error('Missing organization or retrieval ID');
      }

      const response = await graphqlRequest<SyncMetadataItemResponse>(
        SYNC_METADATA_ITEM_MUTATION,
        {
          input: {
            organizationId: orgId,
            retrievalId: retrievalId,
            metadataItemId: input.metadataItemId,
          },
        }
      );

      const { metadataItem, message } = response.fetchMetadataItemFromSalesforce;

      // Check if we got data or null (no changes)
      if (metadataItem === null) {
        throw new NoChangesError(message);
      }

      return metadataItem;
    },
    onSuccess: (data) => {
      console.log('✅ Metadata item synced:', data.fullName || data.id);
      
      // Invalidate the metadata item query to refresh cache
      queryClient.invalidateQueries({ 
        queryKey: ['metadataItem', orgId, data.id] 
      });
      
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      if (error instanceof NoChangesError) {
        console.log('ℹ️ File is already up to date');
        options?.onNoChanges?.();
      } else {
        console.error('❌ Failed to sync metadata item:', error);
        options?.onError?.(error);
      }
    },
  });

  return {
    /** Sync a metadata item from Salesforce */
    syncItem: mutation.mutateAsync,
    /** Whether sync is in progress */
    isPending: mutation.isPending,
    /** Whether sync was successful */
    isSuccess: mutation.isSuccess,
    /** Whether sync failed */
    isError: mutation.isError,
    /** Error if sync failed */
    error: mutation.error,
    /** Reset mutation state */
    reset: mutation.reset,
  };
}